import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  formatDuration,
  formatDistance
} from "./WorkoutTracker/utils/paceCalculations";

// Compiles the last N days of weight, macro and workout logs into one plain-text
// timeline the user can copy straight into a prompt.
//
// All three data sets come from a SINGLE backend call. Fetching workouts through
// WorkoutTracker/services/workoutAPI would be the obvious alternative, but that
// module keeps the active person in module state that only WorkoutTrackerPage
// ever sets - from this page it is null, and every /api/workouts request would
// silently resolve to the account's DEFAULT person rather than the one selected
// here. The server-side endpoint scopes all three to req.personId together.

const EXPORT_DAYS = 30;

// Steps a YYYY-MM-DD string by N days, anchored at UTC noon so a DST transition
// can never skip or repeat a day. Mirrors shiftDateString in backend/app.js.
const shiftDateString = (dateStr, days) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

// Today in the BROWSER's timezone, matching how log_date values are written.
// Deliberately not toISOString(), which would roll over to tomorrow in the
// evening for anyone west of UTC.
const getLocalDateString = () => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0")
  ].join("-");
};

const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
];

const weekdayName = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};

// Rounds to `places` but drops insignificant trailing zeros, so a whole value
// reads "185g" rather than "185.0g" while 184.7 keeps its decimal.
const round = (value, places = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return String(Number(num.toFixed(places)));
};

// One session -> one human phrase. The rules here are the whole point of the
// export, so they are kept together rather than spread through the renderer.
const describeWorkout = (workout) => {
  const parts = [];
  const cardio = workout.cardio || [];

  // Use each exercise's own name so "Running", "Cycling" and "Rowing" stay
  // distinct instead of collapsing into one verb.
  cardio.forEach((c) => {
    let phrase = c.exercise_name || "Cardio";
    if (c.duration_seconds != null) {
      phrase += ` ${formatDuration(c.duration_seconds)}`;
    }
    if (c.distance != null && c.distance > 0) {
      phrase += `, ${formatDistance(c.distance, c.distance_unit || "miles")}`;
    }
    parts.push(phrase);
  });

  if (workout.lift_count > 0) {
    // duration_minutes covers the WHOLE session, so it is only trustworthy as a
    // lifting duration when nothing else happened in that session. Otherwise
    // fall back to the 45-minute average, which is the agreed default for
    // lifting (durations are not logged when lifting).
    const canUseLogged = cardio.length === 0 && workout.duration_minutes != null;
    const minutes = canUseLogged ? Math.round(workout.duration_minutes) : 45;
    parts.push(`Lifted ${minutes} minutes`);
  }

  // A stretch is set-shaped but is not lifting, and gets no invented duration.
  if (parts.length === 0 && workout.stretch_count > 0) {
    parts.push("Stretched");
  }

  // A session can be marked complete with nothing recorded under it (real data
  // has these). Say so rather than implying a workout shape we cannot see.
  if (parts.length === 0) {
    return `${workout.name || "Workout"} (no exercises recorded)`;
  }
  return parts.join("; ");
};

const buildExportText = (payload, personName) => {
  const { start_date, end_date, days = [] } = payload;
  const lines = [];

  lines.push("HEALTH DATA EXPORT");
  if (personName) lines.push(`Person: ${personName}`);
  lines.push(
    `Range: ${start_date} to ${end_date} (${days.length} day${days.length === 1 ? "" : "s"})`
  );
  lines.push("=".repeat(80));
  lines.push("");

  days.forEach((day) => {
    lines.push(`=== ${day.date} (${weekdayName(day.date)}) ===`);

    if (day.weight) {
      const unit = day.weight.unit || "lbs";
      const when = day.weight.time_of_day ? ` (${day.weight.time_of_day})` : "";
      lines.push(`Weight: ${round(day.weight.weight, 1)} ${unit}${when}`);
    } else {
      lines.push("Weight: not logged");
    }

    if (day.macros) {
      const m = day.macros;
      lines.push(
        `Macros: ${round(m.calories)} cal | ${round(m.protein, 1)}g protein | ${round(m.carbs, 1)}g carbs`
      );
      lines.push(
        `        ${round(m.fat, 1)}g fat | ${round(m.fiber, 1)}g fiber | ${round(m.sugar, 1)}g sugar`
      );
    } else {
      lines.push("Macros: not logged");
    }

    if (day.workouts && day.workouts.length > 0) {
      day.workouts.forEach((w) => lines.push(`Workout: ${describeWorkout(w)}`));
    } else {
      lines.push("Workout: none logged");
    }

    if (day.macro_notes) lines.push(`Notes: ${day.macro_notes}`);

    lines.push("");
  });

  return lines.join("\n");
};

const HealthExportModal = ({ show, onClose, personUrl, personName }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportText, setExportText] = useState("");
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);
  const copyTimerRef = useRef(null);

  // end = today (local), start = 29 days back, giving 30 inclusive days.
  const endDate = getLocalDateString();
  const startDate = shiftDateString(endDate, -(EXPORT_DAYS - 1));

  const loadExport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setExportText("");

    try {
      const response = await fetch(
        personUrl(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/export/daily-summary?start_date=${startDate}&end_date=${endDate}`
        ),
        { credentials: "include" }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      const payload = await response.json();
      setExportText(buildExportText(payload, personName));
    } catch (err) {
      console.error("Error building health export:", err);
      setError(err.message || "Failed to build export");
    } finally {
      setIsLoading(false);
    }
  }, [personUrl, startDate, endDate, personName]);

  useEffect(() => {
    if (show) loadExport();
  }, [show, loadExport]);

  // Clear a pending "Copied!" reset if the modal closes first.
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const handleCopy = async () => {
    try {
      // navigator.clipboard needs a secure context; fall back to the old
      // select-and-execCommand path when it is unavailable or blocked.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(exportText);
      } else {
        throw new Error("Clipboard API unavailable");
      }
    } catch (err) {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.select();
      try {
        document.execCommand("copy");
      } catch (fallbackErr) {
        console.error("Copy failed:", fallbackErr);
        return;
      }
    }

    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([exportText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `health-export-${endDate}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content health-export-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>📤 Export Health Data</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="health-export-body">
          <p className="health-export-range">
            Last {EXPORT_DAYS} days · {startDate} to {endDate}
          </p>

          {isLoading && <div className="loading">Gathering your data...</div>}

          {error && (
            <div className="health-export-error">
              <p>{error}</p>
              <button onClick={loadExport} className="refresh-button">
                🔄 Try Again
              </button>
            </div>
          )}

          {!isLoading && !error && (
            <textarea
              ref={textareaRef}
              className="health-export-textarea"
              readOnly
              value={exportText}
              onFocus={(e) => e.target.select()}
            />
          )}
        </div>

        <div className="health-export-footer">
          <button
            onClick={handleCopy}
            className="health-export-btn primary"
            disabled={isLoading || !!error || !exportText}
          >
            {copied ? "✓ Copied!" : "📋 Copy"}
          </button>
          <button
            onClick={handleDownload}
            className="health-export-btn"
            disabled={isLoading || !!error || !exportText}
          >
            ⬇ Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthExportModal;
